import React, { useEffect, useState, useRef, useMemo } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import {
    Button,
    Spinner,
    Card,
    Badge,
    Dropdown,
    Row,
    Col
} from "react-bootstrap";
import {
    FiRefreshCw,
    FiSearch,
    FiX,
    FiChevronDown,
    FiChevronUp,
    FiTrendingUp,
    FiUsers,
    FiClock,
    FiUserCheck,
    FiDownload,
    FiFileText,
    FiFile,
    FiCalendar,
    FiFilter
} from "react-icons/fi";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LoginActivity = () => {
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState("all");
    const [isOpen, setIsOpen] = useState(false);
    const [staffCounts, setStaffCounts] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [activeFilter, setActiveFilter] = useState(null);
    const [statsCollapsed, setStatsCollapsed] = useState(true);
    const hasFetched = useRef(false);
    const [dateRange, setDateRange] = useState({ start: "", end: "" });

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const res = await axios.get("https://www.azzenterprises.com/api/customers/loghistory");

            const logsData = res.data.logs || [];
            const sortedLogs = logsData.sort((a, b) => new Date(b.login_time) - new Date(a.login_time));
            setLogs(sortedLogs);
            setFilteredLogs(sortedLogs);
            setStaffList(res.data.staffList || []);

            const counts = {};
            logsData.forEach(log => {
                counts[log.staff_name] = (counts[log.staff_name] || 0) + 1;
            });
            setStaffCounts(counts);
        } catch (error) {
            console.error("Error fetching logs:", error);
            toast.error("Failed to fetch logs");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (!hasFetched.current) {
            fetchLogs();
            hasFetched.current = true;
        }
    }, []);

    const formatDateDDMMYYYY = (dateString) => {
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return '-';
            return date.toLocaleDateString('en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return '-';
        }
    };

    const activityStats = useMemo(() => {
        const stats = {
            totalLogins: logs.length,
            uniqueStaff: staffList.length,
            todayLogins: 0,
            weekLogins: 0,
            staffActivity: {},
            busiestStaff: { name: "", count: 0 },
            averageLoginsPerStaff: 0,
            last7Days: Array(7).fill(0)
        };

        const now = new Date();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        oneWeekAgo.setHours(0, 0, 0, 0);

        logs.forEach(log => {
            const loginTime = new Date(log.login_time);
            const loginDate = new Date(loginTime.getFullYear(), loginTime.getMonth(), loginTime.getDate());

            if (loginDate.getTime() === today.getTime()) {
                stats.todayLogins++;
            }
            if (loginDate >= oneWeekAgo) {
                stats.weekLogins++;
                const dayDiff = Math.floor((today.getTime() - loginDate.getTime()) / (24 * 60 * 60 * 1000));
                if (dayDiff >= 0 && dayDiff < 7) {
                    stats.last7Days[6 - dayDiff]++;
                }
            }

            if (!stats.staffActivity[log.staff_name]) {
                stats.staffActivity[log.staff_name] = 0;
            }
            stats.staffActivity[log.staff_name]++;
        });

        Object.entries(stats.staffActivity).forEach(([name, count]) => {
            if (count > stats.busiestStaff.count) {
                stats.busiestStaff = { name, count };
            }
        });

        stats.averageLoginsPerStaff = staffList.length > 0
            ? (stats.totalLogins / staffList.length).toFixed(1)
            : 0;

        return stats;
    }, [logs, staffList]);

    useEffect(() => {
        let filtered = logs;

        if (selectedStaff !== "all") {
            filtered = filtered.filter(log => log.staff_name === selectedStaff);
        }

        if (searchTerm) {
            filtered = filtered.filter(
                log =>
                    (log.full_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (log.staff_name || "").toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (dateRange.start && dateRange.end) {
            const startDate = new Date(dateRange.start);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(dateRange.end);
            endDate.setHours(23, 59, 59, 999);

            filtered = filtered.filter(log => {
                const logDate = new Date(log.login_time);
                return logDate >= startDate && logDate <= endDate;
            });
        }

        if (activeFilter) {
            switch (activeFilter.type) {
                case 'today':
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const tomorrow = new Date(today);
                    tomorrow.setDate(tomorrow.getDate() + 1);

                    filtered = filtered.filter(log => {
                        const logDate = new Date(log.login_time);
                        return logDate >= today && logDate < tomorrow;
                    });
                    break;

                case 'week':
                    const oneWeekAgo = new Date();
                    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                    oneWeekAgo.setHours(0, 0, 0, 0);

                    filtered = filtered.filter(log => {
                        const logDate = new Date(log.login_time);
                        return logDate >= oneWeekAgo;
                    });
                    break;

                default:
                    break;
            }
        }

        setFilteredLogs(filtered);
    }, [logs, selectedStaff, searchTerm, dateRange, activeFilter]);

    const clearFilters = () => {
        setSelectedStaff("all");
        setSearchTerm("");
        setDateRange({ start: "", end: "" });
        setActiveFilter(null);
    };

    const handleExportExcel = () => {
        const formattedData = filteredLogs.map(log => ({
            'Customer Name': log.full_name || '-',
            'Staff Name': log.staff_name || '-',
            'Date': formatDateDDMMYYYY(log.login_time),
            'Time': new Date(log.login_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        }));

        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Login Activity");
        XLSX.writeFile(wb, "login_activity.xlsx");
        toast.success("Exported to Excel");
    };

    const handleExportCSV = () => {
        const formattedData = filteredLogs.map(log => ({
            'Customer Name': log.full_name || '-',
            'Staff Name': log.staff_name || '-',
            'Date': new Date(log.login_time).toLocaleDateString(),
            'Time': new Date(log.login_time).toLocaleTimeString(),
        }));

        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Login Activity");
        XLSX.writeFile(wb, "login_activity.csv");
        toast.success("Exported to CSV");
    };

    const handleExportPDF = () => {
        if (filteredLogs.length === 0) {
            toast.warn("No data to export");
            return;
        }

        try {
            const doc = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: 'a4'
            });

            const margin = 2;
            const pageWidth = doc.internal.pageSize.width;

            doc.setFontSize(14);
            doc.setTextColor(30, 41, 59);
            doc.text("Login Activity Report", margin, margin + 8);

            doc.setFontSize(7);
            doc.setTextColor(100, 100, 100);
            doc.text(`Total: ${filteredLogs.length} records | Generated: ${new Date().toLocaleDateString()}`, margin, margin + 14);

            const pdfData = filteredLogs.map((log) => [
                log.full_name || "-",
                log.staff_name || "-",
                formatDateDDMMYYYY(log.login_time),
                new Date(log.login_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                log.customer_id || "-"
            ]);

            const headers = ["Customer Name", "Staff Name", "Date", "Time"];

            autoTable(doc, {
                head: [headers],
                body: pdfData,
                startY: margin + 18,
                styles: {
                    fontSize: 8,
                    cellPadding: 1,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.05,
                    textColor: [30, 30, 30]
                },
                headStyles: {
                    fillColor: [59, 130, 246],
                    textColor: 255,
                    fontStyle: 'bold',
                    fontSize: 9
                },
                margin: { left: margin, right: margin },
                tableWidth: pageWidth - (margin * 2),
                theme: 'grid'
            });

            doc.save(`login_activity_${new Date().toISOString().slice(0, 10)}.pdf`);
            toast.success("PDF exported successfully");
        } catch (error) {
            console.error("PDF export error:", error);
            toast.error("Failed to generate PDF");
        }
    };

    const columns = [
        {
            name: "#",
            selector: (row, index) => index + 1,
            sortable: true,
            width: "60px"
        },
        {
            name: "Customer Name",
            selector: (row) => row.full_name,
            sortable: true,
            grow: 2,
            cell: (row) => (
                <div>{row.full_name || "-"}</div>
            )
        },
        {
            name: "Staff Name",
            selector: (row) => row.staff_name,
            sortable: true,
            width: "150px",
            cell: (row) => {
                const staffColor = staffColors.find(color =>
                    color.staff === row.staff_name?.toLowerCase()
                ) || { badgeBg: '#f3f4f6', badgeBorder: '#d1d5db', badgeText: '#374151' };

                return (
                    <div
                        className="px-2 py-1 border rounded"
                        style={{
                            backgroundColor: staffColor.badgeBg,
                            color: staffColor.badgeText,
                            borderColor: staffColor.badgeBorder,
                            fontSize: '13px',
                            fontWeight: 500,
                        }}
                    >
                        {row.staff_name || "-"}
                    </div>
                );
            },
            right: true,
        },

        {
            name: "Date",
            id: "date",
            selector: (row) => formatDateDDMMYYYY(row.login_time),
            sortable: true,
            cell: (row) => (
                <div className="d-flex align-items-center gap-1">
                    <span>{formatDateDDMMYYYY(row.login_time)}</span>
                </div>
            ),
            width: "120px",
            right: true,
        },
        {
            name: "Time",
            selector: (row) => new Date(row.login_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            sortable: true,
            cell: (row) => (
                <div className="d-flex align-items-center gap-1">
                    <span>{new Date(row.login_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
            ),
            width: "120px",
            right: true,
        }
    ];


    const StatCard = ({ title, value, subtitle, icon, color, filterType, filterValue, isActive, onFilter }) => {
        const hexToRgba = (hex, alpha) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        return (
            <Card
                className={`border-0 shadow-sm h-100 ${isActive ? 'active-stat-card' : ''}`}
                style={{
                    cursor: onFilter ? 'pointer' : 'default',
                    border: isActive ? `2px solid ${color}` : '1px solid #e5e7eb',
                    backgroundColor: isActive ? hexToRgba(color, 0.08) : '#ffffff',
                    transform: isActive ? 'translateY(-2px)' : 'none',
                    boxShadow: isActive ? `0 4px 12px ${hexToRgba(color, 0.15)}` : '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease'
                }}
                onClick={onFilter}
            >
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{
                                width: "48px",
                                height: "48px",
                                backgroundColor: isActive ? color : `${color}15`,
                                color: isActive ? '#fff' : color,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {React.cloneElement(icon, {
                                size: 20,
                                color: isActive ? '#fff' : color
                            })}
                        </div>
                        <div>
                            <h6 className="mb-1" style={{
                                fontSize: "0.875rem",
                                color: isActive ? color : '#6b7280',
                                fontWeight: isActive ? '600' : '400'
                            }}>
                                {title}
                            </h6>
                            <h4 className="fw-bold mb-0" style={{
                                color: isActive ? color : '#1f2937',
                                fontSize: "1.5rem"
                            }}>
                                {value}
                            </h4>
                            {subtitle && (
                                <small style={{
                                    fontSize: "0.75rem",
                                    color: isActive ? color : '#6b7280'
                                }}>
                                    {subtitle}
                                </small>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };
    const staffColors = useMemo(() => {
        const colors = [
            { bg: '#f8fafc', text: '#475569', hover: '#f1f5f9', badgeBg: '#f1f5f9', badgeBorder: '#cbd5e1', badgeText: '#475569' },
            { bg: '#f1f5f9', text: '#334155', hover: '#e2e8f0', badgeBg: '#e2e8f0', badgeBorder: '#94a3b8', badgeText: '#334155' },
            { bg: '#fafafa', text: '#525252', hover: '#f5f5f5', badgeBg: '#f5f5f5', badgeBorder: '#d1d5db', badgeText: '#525252' },
            { bg: '#f9fafb', text: '#374151', hover: '#f3f4f6', badgeBg: '#f3f4f6', badgeBorder: '#d1d5db', badgeText: '#374151' },
            { bg: '#fefefe', text: '#404040', hover: '#f5f5f5', badgeBg: '#f8fafc', badgeBorder: '#e5e7eb', badgeText: '#404040' },
            { bg: '#fcfcfc', text: '#4b5563', hover: '#f3f4f6', badgeBg: '#f1f5f9', badgeBorder: '#cbd5e1', badgeText: '#4b5563' },
            { bg: '#f7fafc', text: '#4a5568', hover: '#edf2f7', badgeBg: '#e2e8f0', badgeBorder: '#94a3b8', badgeText: '#4a5568' },
            { bg: '#f0f4f8', text: '#486581', hover: '#e0e7ff', badgeBg: '#dbeafe', badgeBorder: '#93c5fd', badgeText: '#1e40af' }
        ];

        const uniqueStaff = [...new Set(staffList)];
        return uniqueStaff.map((staff, index) => ({
            staff: staff.toLowerCase(),
            ...colors[index % colors.length]
        }));
    }, [staffList]);

    return (
        <div className="container-fluid py-2">
            <ToastContainer position="top-right" autoClose={1500} theme="light" />

            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                        <div className="flex-grow-1">
                            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3 mb-2">
                                <p className="fw-bold mb-0 text-muted" style={{ fontSize: "0.8rem" }}>
                                    Monitor customer login activity and staff performance
                                </p>
                                {activeFilter && (
                                    <div className="d-flex align-items-center">
                                        <Badge bg="primary" className="px-2 py-1 d-flex align-items-center gap-1">
                                            <span className="text-nowrap">
                                                Filter: {activeFilter.type === 'today'
                                                    ? "Today's Logins"
                                                    : activeFilter.type === 'week'
                                                        ? "Last 7 Days"
                                                        : activeFilter.type}
                                            </span>
                                            <Button
                                                variant="link"
                                                className="p-0 text-white lh-1 fs-6"
                                                onClick={() => setActiveFilter(null)}
                                            >
                                                <FiX size={12} />
                                            </Button>
                                        </Badge>
                                    </div>
                                )}
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <div
                                        className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-primary"
                                        style={{ backgroundColor: '#dbeafe' }}
                                    >
                                        <div
                                            className="rounded-circle"
                                            style={{ width: '6px', height: '6px', backgroundColor: '#3b82f6' }}
                                        />
                                        <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#1e40af' }}>
                                            {staffList.length} Staff Members
                                        </span>
                                    </div>
                                    <div
                                        className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-success"
                                        style={{ backgroundColor: '#dcfce7' }}
                                    >
                                        <div
                                            className="rounded-circle"
                                            style={{ width: '6px', height: '6px', backgroundColor: '#16a34a' }}
                                        />
                                        <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#166534' }}>
                                            {activityStats.todayLogins} Today
                                        </span>
                                    </div>
                                    <div
                                        className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-warning"
                                        style={{ backgroundColor: '#fef3c7' }}
                                    >
                                        <div
                                            className="rounded-circle"
                                            style={{ width: '6px', height: '6px', backgroundColor: '#f59e0b' }}
                                        />
                                        <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#92400e' }}>
                                            {activityStats.weekLogins} This Week
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
                            <Dropdown>
                                <Dropdown.Toggle
                                    variant="outline-primary"
                                    size="sm"
                                    className="d-flex align-items-center gap-2 border"
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    <FiDownload size={14} />
                                    <span className="d-none d-sm-inline">Export</span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={handleExportCSV} className="py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiFileText size={14} className="text-primary" />
                                            <div className="text-start" style={{ fontSize: '0.8rem' }}>
                                                <div className="fw-medium">CSV Export</div>
                                                <small className="text-muted">Comma-separated values</small>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handleExportExcel} className="py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiFile size={14} className="text-primary" />
                                            <div className="text-start" style={{ fontSize: '0.8rem' }}>
                                                <div className="fw-medium">Excel Export</div>
                                                <small className="text-muted">Microsoft Excel format</small>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handleExportPDF} className="py-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <FiDownload size={14} className="text-primary" />
                                            <div className="text-start" style={{ fontSize: '0.8rem' }}>
                                                <div className="fw-medium">PDF Export</div>
                                                <small className="text-muted">Portable Document Format</small>
                                            </div>
                                        </div>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>

                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="d-flex align-items-center gap-2 border"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={fetchLogs}
                                    disabled={loading}
                                >
                                    <FiRefreshCw size={14} className={loading ? "spinning" : ""} />
                                    <span className="d-none d-sm-inline">Refresh</span>
                                </Button>

                                {(selectedStaff !== "all" || searchTerm || dateRange.start || dateRange.end || activeFilter) && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="d-flex align-items-center gap-2 border"
                                        style={{ fontSize: '0.75rem' }}
                                        onClick={clearFilters}
                                    >
                                        <FiX size={14} />
                                        <span className="d-none d-sm-inline">Clear Filters</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Card className="border-0 shadow-sm mb-2">
                <Card.Body className="p-0">
                    <div
                        className="p-3 border-bottom bg-gray-50 d-flex justify-content-between align-items-center cursor-pointer"
                        onClick={() => setStatsCollapsed(!statsCollapsed)}
                        style={{
                            cursor: 'pointer',
                            backgroundColor: '#d3ebffff'
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <FiTrendingUp size={14} className="text-primary me-2" />
                            <span className="fw-semibold" style={{ fontSize: 12 }}>
                                Activity Statistics
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <FiChevronDown
                                size={14}
                                className={`text-muted transition-transform ${statsCollapsed ? '' : 'rotate-180'}`}
                                style={{
                                    transition: 'transform 0.3s ease',
                                    transform: statsCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'
                                }}
                            />
                        </div>
                    </div>

                    {!statsCollapsed && (
                        <div className="pt-4">
                            <Row className="g-3">
                                <Col md={3}>
                                    <StatCard
                                        title="Total Logins"
                                        value={activityStats.totalLogins}
                                        icon={<FiClock />}
                                        color="#374151"
                                        filterType="all"
                                        isActive={!activeFilter && selectedStaff === "all"}
                                        onFilter={clearFilters}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Today's Logins"
                                        value={activityStats.todayLogins}
                                        subtitle={`${activityStats.weekLogins} this week`}
                                        icon={<FiUsers />}
                                        color="#8b5cf6"
                                        filterType="today"
                                        isActive={activeFilter?.type === 'today'}
                                        onFilter={() => setActiveFilter({ type: 'today' })}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Staff Members"
                                        value={activityStats.uniqueStaff}
                                        subtitle={`${activityStats.averageLoginsPerStaff} avg per staff`}
                                        icon={<FiUserCheck />}
                                        color="#16a34a"
                                        filterType="staff"
                                        isActive={false}
                                    />
                                </Col>
                                <Col md={3}>
                                    <StatCard
                                        title="Most Active Staff"
                                        value={activityStats.busiestStaff.name || "N/A"}
                                        subtitle={`${activityStats.busiestStaff.count} logins`}
                                        icon={<FiTrendingUp />}
                                        color="#dc2626"
                                        filterType="staff"
                                        isActive={false}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="p-3 border-bottom bg-gray-50">
                        <div className="row g-2 align-items-center">
                            <div className="col-md-6">
                                <div className="input-group" style={{ maxWidth: "400px" }}>
                                    <span className="input-group-text bg-white border-end-0">
                                        <FiSearch size={16} className="text-gray-500" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search by customer or staff name"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ fontSize: "0.875rem" }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex justify-content-end gap-2 flex-wrap">
                                    <Dropdown onToggle={(open) => setIsOpen(open)}>
                                        <Dropdown.Toggle
                                            variant="outline-secondary"
                                            size="sm"
                                            id="dropdown-staff"
                                            className="d-flex align-items-center justify-content-between gap-2 border"
                                            style={{ minWidth: "160px", fontSize: "13px" }}
                                        >
                                            <span>{selectedStaff === "all" ? "All Staff" : selectedStaff}</span>
                                            <FiChevronDown size={14} className={isOpen ? "rotate-180" : ""} />
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="shadow-lg border-0 rounded-3">
                                            <Dropdown.Item
                                                key="all"
                                                active={selectedStaff === "all"}
                                                onClick={() => setSelectedStaff("all")}
                                                className="py-2 px-3"
                                                style={{ fontSize: '13px' }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span>All Staff</span>
                                                    <Badge bg="primary" className="ms-2">
                                                        {logs.length}
                                                    </Badge>
                                                </div>
                                            </Dropdown.Item>
                                            {staffList.map((staff) => (
                                                <Dropdown.Item
                                                    key={staff}
                                                    active={selectedStaff === staff}
                                                    onClick={() => setSelectedStaff(staff)}
                                                    className="py-2 px-3"
                                                    style={{ fontSize: '13px' }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span>{staff}</span>
                                                        <Badge bg="success" className="ms-2">
                                                            {staffCounts[staff] || 0}
                                                        </Badge>
                                                    </div>
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Menu>
                                    </Dropdown>

                                    <div className="d-flex gap-1">
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            style={{ maxWidth: "120px", fontSize: "0.75rem" }}
                                            value={dateRange.start}
                                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                                            placeholder="From"
                                        />
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            style={{ maxWidth: "120px", fontSize: "0.75rem" }}
                                            value={dateRange.end}
                                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                                            placeholder="To"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-3">
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center py-5">
                                <Spinner animation="border" variant="secondary" />
                                <span className="ms-2 text-muted">Loading login activity...</span>
                            </div>
                        ) : filteredLogs.length > 0 ? (
                            <DataTable
                                keyField="id"
                                columns={columns}
                                data={filteredLogs}
                                progressPending={loading}
                                pagination
                                responsive
                                highlightOnHover
                                striped
                                dense
                                defaultSortFieldId="date"
                                defaultSortAsc={false}
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                                persistTableHead
                                fixedHeader
                                fixedHeaderScrollHeight="calc(100vh - 100px)"
                                conditionalRowStyles={
                                    staffColors.map(({ staff, bg, text, hover }) => ({
                                        when: row => row.staff_name?.toLowerCase() === staff,
                                        style: {
                                            backgroundColor: bg,
                                            color: text,
                                            '&:hover': {
                                                backgroundColor: hover
                                            }
                                        }
                                    }))
                                }
                                customStyles={{
                                    headCells: {
                                        style: {
                                            backgroundColor: "#f9fafb",
                                            fontWeight: "600",
                                            fontSize: "0.75rem",
                                            textTransform: "uppercase",
                                            color: "#374151",
                                            borderBottom: "1px solid #e5e7eb"
                                        }
                                    },
                                    cells: {
                                        style: {
                                            fontSize: "0.875rem",
                                            color: "#374151",
                                            padding: '12px'
                                        }
                                    }
                                }}
                            />

                        ) : (
                            <div className="text-center my-5">
                                <p className="text-muted">No login records found</p>
                                <Button variant="outline-primary" onClick={fetchLogs}>
                                    <FiRefreshCw className="me-1" /> Refresh Data
                                </Button>
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default LoginActivity;